import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { typeLabel, money, STATUS_META } from "@/data/constants";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Plus, Trash2, MapPin, Loader2, Building2, Eye, Pencil, ShieldCheck, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const FALLBACK =
  "https://images.unsplash.com/photo-1628012209120-d9db7abf7eab?crop=entropy&cs=srgb&fm=jpg&q=85&w=940";

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [listings, setListings] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data }, h] = await Promise.all([
        api.get("/my/listings"),
        api.get("/my/history").catch(() => ({ data: [] })),
      ]);
      setListings(data);
      setHistory(h.data);
    } catch {
      setListings([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const reactivate = async (listingId) => {
    try {
      await api.post(`/my/history/${listingId}/reactivate`);
      toast.success("Listing re-activated");
      load();
    } catch (e) {
      toast.error("Could not re-activate listing");
    }
  };

  useEffect(() => {
    load();
  }, [load]);

  const remove = async (id) => {
    try {
      await api.delete(`/listings/${id}`);
      setListings((p) => p.filter((l) => l.id !== id));
      toast.success("Listing deleted");
    } catch {
      toast.error("Could not delete listing");
    }
  };

  const changeStatus = async (id, status) => {
    try {
      await api.patch(`/listings/${id}/status`, { status });
      setListings((p) => p.map((l) => (l.id === id ? { ...l, status } : l)));
      toast.success("Status updated");
    } catch {
      toast.error("Could not update status");
    }
  };

  return (
    <div className="mx-auto max-w-7xl px-5 py-10 lg:px-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <span className="label-caps text-[#D4A373]">{user?.role === "agent" ? "Agent" : "Owner"} dashboard</span>
          <h1 className="mt-2 font-display text-3xl font-medium tracking-tight text-[#1A1A1A]">
            Welcome back, {user?.name?.split(" ")[0] || "there"}
          </h1>
          <p className="mt-1 text-[#6B7280]">Manage your property listings.</p>
        </div>
        <div className="flex gap-2">
          <Link to="/get-verified" data-testid="dashboard-verify-btn">
            <Button variant="outline" className="rounded-full border-[#1D4ED8] text-[#1D4ED8] hover:bg-[#1D4ED8]/5">
              <ShieldCheck className="mr-1.5 h-4 w-4" /> Get verified
            </Button>
          </Link>
          <Button data-testid="dashboard-create-btn" onClick={() => navigate("/create")} className="rounded-full bg-[#2C3830] text-white hover:bg-[#3A4A40]">
            <Plus className="mr-1.5 h-4 w-4" /> New listing
          </Button>
        </div>
      </div>

      <div className="mt-8">
        {loading ? (
          <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#2C3830]" /></div>
        ) : listings.length === 0 ? (
          <div data-testid="dashboard-empty" className="flex flex-col items-center gap-3 rounded-2xl border border-dashed border-border py-24 text-center">
            <Building2 className="h-10 w-10 text-[#D4A373]" />
            <p className="font-display text-xl text-[#2C3830]">No listings yet</p>
            <p className="text-sm text-[#6B7280]">Create your first property listing to get started.</p>
            <Button onClick={() => navigate("/create")} className="mt-2 rounded-full bg-[#2C3830] text-white">
              <Plus className="mr-1.5 h-4 w-4" /> List a property
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
            {listings.map((l) => (
              <div key={l.id} data-testid={`my-listing-${l.id}`} className="overflow-hidden rounded-2xl border border-border/60 bg-white">
                <div className="relative aspect-[4/3] bg-[#EDE9E1]">
                  <img src={l.photos?.length ? fileUrl(l.photos[0]) : FALLBACK} alt={l.title} className="h-full w-full object-cover" onError={(e) => (e.currentTarget.src = FALLBACK)} />
                  <div className="absolute left-3 top-3 flex gap-2">
                    <Badge className="rounded-full border-0 bg-[#2C3830] capitalize text-white">For {l.listing_type}</Badge>
                    {l.status && l.status !== "active" && (
                      <Badge className="rounded-full border-0 text-white" style={{ backgroundColor: STATUS_META[l.status].color }}>{STATUS_META[l.status].label}</Badge>
                    )}
                  </div>
                </div>
                <div className="space-y-3 p-4">
                  {["rented", "sold"].includes(l.status) && (() => {
                    const days = l.status_changed_at ? Math.floor((Date.now() - new Date(l.status_changed_at)) / 86400000) : 0;
                    const left = Math.max(0, 30 - days);
                    return (
                      <div data-testid={`nudge-${l.id}`} className="rounded-xl bg-[#FEF3C7] px-3 py-2 text-xs leading-relaxed text-[#92400E]">
                        Marked {STATUS_META[l.status].label.toLowerCase()} {days} day{days === 1 ? "" : "s"} ago. You can remove it now to keep your list tidy — otherwise it auto-removes in {left} day{left === 1 ? "" : "s"}.
                      </div>
                    );
                  })()}
                  <h3 className="font-display text-lg font-medium text-[#1A1A1A] line-clamp-1">{l.title}</h3>
                  <div className="flex items-center gap-1.5 text-sm text-[#6B7280]">
                    <MapPin className="h-4 w-4 text-[#D4A373]" /> {l.town}, {l.county}
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="font-display text-lg font-semibold text-[#2C3830]">{money(l.price, l.currency)}</span>
                    <span className="text-xs text-[#6B7280]">{typeLabel(l.property_type)}</span>
                  </div>
                  <div className="flex items-center gap-2 border-t border-border/60 pt-3">
                    <Select value={l.status || "active"} onValueChange={(v) => changeStatus(l.id, v)}>
                      <SelectTrigger data-testid={`status-${l.id}`} className="h-9 flex-1 rounded-full text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active">Available</SelectItem>
                        <SelectItem value="rented">Rented</SelectItem>
                        <SelectItem value="sold">Sold</SelectItem>
                      </SelectContent>
                    </Select>
                    <Link to={`/edit/${l.id}`}>
                      <Button data-testid={`edit-${l.id}`} variant="outline" size="icon" className="rounded-full"><Pencil className="h-4 w-4" /></Button>
                    </Link>
                    <Link to={`/listing/${l.id}`}>
                      <Button data-testid={`view-${l.id}`} variant="outline" size="icon" className="rounded-full"><Eye className="h-4 w-4" /></Button>
                    </Link>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button data-testid={`delete-${l.id}`} variant="ghost" size="icon" className="rounded-full text-[#6B7280] hover:bg-destructive/10 hover:text-destructive"><Trash2 className="h-4 w-4" /></Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent className="rounded-2xl">
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete this listing?</AlertDialogTitle>
                          <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel className="rounded-full">Cancel</AlertDialogCancel>
                          <AlertDialogAction data-testid={`confirm-delete-${l.id}`} onClick={() => remove(l.id)} className="rounded-full bg-destructive text-white hover:bg-destructive/90">Delete</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {history.length > 0 && (
        <div className="mt-12" data-testid="past-units">
          <h2 className="font-display text-xl font-medium text-[#1A1A1A]">Past units</h2>
          <p className="mt-1 text-sm text-[#6B7280]">Units you marked rented or sold. If one frees up again, re-list it in one click.</p>
          <div className="mt-4 divide-y divide-border/60 overflow-hidden rounded-2xl border border-border/60 bg-white">
            {history.map((h) => (
              <div key={h.listing_id} data-testid={`history-${h.listing_id}`} className="flex flex-wrap items-center justify-between gap-3 p-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-[#2C3830] line-clamp-1">{h.title}</span>
                    <Badge className="rounded-full border-0 capitalize text-white" style={{ backgroundColor: STATUS_META[h.status]?.color || "#6B7280" }}>{h.status}</Badge>
                  </div>
                  <div className="mt-0.5 text-xs text-[#6B7280]">
                    {h.town}, {h.county} · {money(h.price, h.currency)} · closed {h.closed_at ? new Date(h.closed_at).toLocaleDateString() : "—"}
                  </div>
                </div>
                <Button
                  data-testid={`reactivate-${h.listing_id}`}
                  onClick={() => reactivate(h.listing_id)}
                  variant="outline"
                  size="sm"
                  className="rounded-full border-[#2C3830] text-[#2C3830] hover:bg-[#2C3830]/5"
                >
                  <RefreshCw className="mr-1.5 h-4 w-4" /> Re-list
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
